import { FetchParams, Middleware } from 'api'
import { loginCustomer } from 'queries/queries'
import { getProjectId } from 'utilities/get-project-id'
import { clearLocalData, getLocalLoginRequest, getLocalToken } from 'utilities/use-local-data'
import { appendProjectId } from './use-navigate-project'

const abortController = new AbortController()
export const httpMiddleware: Middleware = {
  pre: ({ init: initOptions, url }) => {
    const token = getLocalToken()

    const init: RequestInit = {
      signal: abortController.signal,
      ...initOptions,
      headers: {
        'Access-Control-Allow-Origin': window.location.origin,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(!!token && {
          Authorization: `Bearer ${token}`,
        }),
        ...initOptions.headers,
      },
    }

    // Add cancel property to promise object for react-query to cancel request if aborted
    const promise: Promise<FetchParams> & {
      cancel?: typeof abortController.abort
    } = Promise.resolve({
      url,
      init: { ...initOptions, ...init },
    })

    promise.cancel = abortController.abort
    return promise
  },
  post: async ({ fetch, init, response, url }) => {
    if (response.ok) {
      // No content
      if (response.status === 204) {
        return Promise.reject({ status: response.status, message: 'No content' })
      }
      return Promise.resolve(response)
    }

    // Unauthorizede HTTP (Token might have expired)
    if (response.status === 401 || response.status === 403) {
      console.warn('Error status 401, try check session')

      const authenticateCustomerModel = getLocalLoginRequest()
      const projectId = getProjectId()
      if (!authenticateCustomerModel) {
        console.warn('no requestModel found in local storage')
        clearLocalData()
        window.location.href = appendProjectId(window.location.origin).toString()
        return
      }

      const token = await loginCustomer({ projectId, authenticateCustomerModel }).then(
        (authenticateCustomer) => authenticateCustomer.token,
        (error) => {
          console.warn('error when refreshing token', error)
          return undefined
        }
      )

      if (!token) {
        console.warn('No new token received, redirect user to login screen')
        clearLocalData()
        window.location.href = appendProjectId('/').toString()
        return
      }

      console.warn('Successfully renewed token', token)
      // Try reftech with new token
      return fetch(url, {
        ...init,
        headers: { ...init.headers, Authorization: `Bearer ${token}` },
      })
    }
    // else if (response.status === 404) {
    // throw Error('Need implementation')
    // navigate('/not-found', { replace: true })
    // }

    const json = await response.json()

    // Add properties for different status codes
    const statusContainer = {
      ...(response.status === 400 && { errors: json.errors }), // Bad Request (Typically form error)
      ...(response.status === 409 && { status: json.detail }), // Conflict
    }

    // If no keys were added, reject json, else reject status container
    if (Object.keys(statusContainer).length === 0) return Promise.reject(json)
    return Promise.reject(statusContainer)
  },
}
